import { FC, useState, useCallback } from "react";
import {
    PhotoMetaBulkUpdateRequest,
    WeatherTypes,
    TimeOfDay,
    SceneType,
    TargetTypes,
    FHWAClasses,
    getSceneTypeDisplayName,
    getTimeOfDayDisplayName,
    Dialog,
} from "@app/shared";
import * as PhotoMetaApi from "@app/shared/src/api/PhotoMetaControllerClient.gen";
import { CheckboxInput, EnumSelect, FlagEnumCheckboxInput, Input, NumericInput } from "../../../shared/inputs";

interface Props {
    show: boolean;
    selectedIds: string[];
    onClose: () => void;
    onUpdated: () => void;
}

interface FieldState<T> {
    enabled: boolean;
    value: T;
}

export const PhotoBulkEditModal: FC<Props> = ({ show, selectedIds, onClose, onUpdated }) => {
    const [weather, setWeather] = useState<FieldState<number>>({ enabled: false, value: 0 });
    const [timeOfDay, setTimeOfDay] = useState<FieldState<number>>({ enabled: false, value: 0 });
    const [scene, setScene] = useState<FieldState<number>>({ enabled: false, value: 0 });
    const [targetTypesInView, setTargetTypesInView] = useState<FieldState<number>>({ enabled: false, value: 0 });
    const [fhwaClassesInView, setFhwaClassesInView] = useState<FieldState<number>>({ enabled: false, value: 0 });
    const [needsReview, setNeedsReview] = useState<FieldState<boolean>>({ enabled: false, value: false });
    const [dewarpedImage, setDewarpedImage] = useState<FieldState<boolean>>({ enabled: false, value: false });
    const [numLanes, setNumLanes] = useState<FieldState<number>>({ enabled: false, value: 0 });
    const [addedToTrainingDataset, setAddedToTrainingDataset] = useState<FieldState<boolean>>({ enabled: false, value: false });
    const [trainingDatasetName, setTrainingDatasetName] = useState<FieldState<string>>({ enabled: false, value: "" });
    const [saving, setSaving] = useState(false);

    const handleApply = useCallback(async () => {
        setSaving(true);
        try {
            const req = new PhotoMetaBulkUpdateRequest();
            req.ids = selectedIds;
            if (weather.enabled) req.weather = weather.value;
            if (timeOfDay.enabled) req.timeOfDay = timeOfDay.value as TimeOfDay;
            if (scene.enabled) req.scene = scene.value as SceneType;
            if (targetTypesInView.enabled) req.targetTypesInView = targetTypesInView.value;
            if (fhwaClassesInView.enabled) req.fhwaClassesInView = fhwaClassesInView.value;
            if (needsReview.enabled) req.needsReview = needsReview.value;
            if (dewarpedImage.enabled) req.dewarpedImage = dewarpedImage.value;
            if (numLanes.enabled) req.numLanes = numLanes.value;
            if (addedToTrainingDataset.enabled) req.addedToTrainingDataset = addedToTrainingDataset.value;
            if (trainingDatasetName.enabled) req.trainingDatasetName = trainingDatasetName.value;

            await PhotoMetaApi.bulkUpdate(req);
            onUpdated();
        } finally {
            setSaving(false);
        }
    }, [selectedIds, weather, timeOfDay, scene, targetTypesInView, fhwaClassesInView, needsReview, dewarpedImage, numLanes, addedToTrainingDataset, trainingDatasetName, onUpdated]);

    const hasChanges = weather.enabled || timeOfDay.enabled || scene.enabled || targetTypesInView.enabled || fhwaClassesInView.enabled || needsReview.enabled || dewarpedImage.enabled || numLanes.enabled || addedToTrainingDataset.enabled || trainingDatasetName.enabled;

    return (
        <Dialog
            show={show}
            title="Bulk Edit Metadata"
            onOkClick={handleApply}
            onCancelClick={onClose}
            okButtonText={saving ? "Applying..." : `Apply to ${selectedIds.length} Photo${selectedIds.length !== 1 ? "s" : ""}`}
            disableOkButton={saving || !hasChanges}
            className="w-full max-w-lg"
        >
            <p className="text-sm text-gray-500 mb-4">
                Update metadata for <span className="font-semibold text-gray-700">{selectedIds.length}</span> selected photo{selectedIds.length !== 1 ? "s" : ""}.
                Check a field to include it in the update.
            </p>

            <div className="space-y-3">
                <div className="space-y-1">
                    <CheckboxInput
                        label="Time of Day"
                        checked={timeOfDay.enabled}
                        onChange={() => setTimeOfDay({ ...timeOfDay, enabled: !timeOfDay.enabled })}
                    />
                    {timeOfDay.enabled && (
                        <div className="ml-6">
                            <EnumSelect
                                enumType={TimeOfDay}
                                value={timeOfDay.value}
                                onChange={(val) => setTimeOfDay({ ...timeOfDay, value: val ?? 0 })}
                                nameToDisplayConverter={getTimeOfDayDisplayName}
                                sort
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Weather"
                        checked={weather.enabled}
                        onChange={() => setWeather({ ...weather, enabled: !weather.enabled })}
                    />
                    {weather.enabled && (
                        <div className="ml-6">
                            <FlagEnumCheckboxInput
                                enumType={WeatherTypes}
                                value={weather.value}
                                onChange={(v) => setWeather({ ...weather, value: v ?? 0 })}
                                keyFilter={(k) => typeof k === "number" && k > 0}
                                sort
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Scene"
                        checked={scene.enabled}
                        onChange={() => setScene({ ...scene, enabled: !scene.enabled })}
                    />
                    {scene.enabled && (
                        <div className="ml-6">
                            <EnumSelect
                                enumType={SceneType}
                                value={scene.value}
                                onChange={(val) => setScene({ ...scene, value: val ?? 0 })}
                                nameToDisplayConverter={getSceneTypeDisplayName}
                                sort
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Target Types In View"
                        checked={targetTypesInView.enabled}
                        onChange={() => setTargetTypesInView({ ...targetTypesInView, enabled: !targetTypesInView.enabled })}
                    />
                    {targetTypesInView.enabled && (
                        <div className="ml-6">
                            <FlagEnumCheckboxInput
                                enumType={TargetTypes}
                                value={targetTypesInView.value}
                                onChange={(v) => setTargetTypesInView({ ...targetTypesInView, value: v ?? 0 })}
                                keyFilter={(k) => typeof k === "number" && k > 0}
                                sort
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="FHWA Classes In View"
                        checked={fhwaClassesInView.enabled}
                        onChange={() => setFhwaClassesInView({ ...fhwaClassesInView, enabled: !fhwaClassesInView.enabled })}
                    />
                    {fhwaClassesInView.enabled && (
                        <div className="ml-6">
                            <FlagEnumCheckboxInput
                                enumType={FHWAClasses}
                                value={fhwaClassesInView.value}
                                onChange={(v) => setFhwaClassesInView({ ...fhwaClassesInView, value: v ?? 0 })}
                                keyFilter={(k) => typeof k === "number" && k > 0}
                                sort
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Needs Review"
                        checked={needsReview.enabled}
                        onChange={() => setNeedsReview({ ...needsReview, enabled: !needsReview.enabled })}
                    />
                    {needsReview.enabled && (
                        <div className="ml-6">
                            <CheckboxInput
                                label={`Set to ${needsReview.value ? "Yes" : "No"}`}
                                checked={needsReview.value}
                                onChange={() => setNeedsReview({ ...needsReview, value: !needsReview.value })}
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Dewarped"
                        checked={dewarpedImage.enabled}
                        onChange={() => setDewarpedImage({ ...dewarpedImage, enabled: !dewarpedImage.enabled })}
                    />
                    {dewarpedImage.enabled && (
                        <div className="ml-6">
                            <CheckboxInput
                                label={`Set to ${dewarpedImage.value ? "Yes" : "No"}`}
                                checked={dewarpedImage.value}
                                onChange={() => setDewarpedImage({ ...dewarpedImage, value: !dewarpedImage.value })}
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Num Lanes"
                        checked={numLanes.enabled}
                        onChange={() => setNumLanes({ ...numLanes, enabled: !numLanes.enabled })}
                    />
                    {numLanes.enabled && (
                        <div className="ml-6">
                            <NumericInput
                                value={numLanes.value}
                                onChange={(val) => setNumLanes({ ...numLanes, value: val ?? 0 })}
                                min={0}
                                allowFloat={false}
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Added To Training Dataset"
                        checked={addedToTrainingDataset.enabled}
                        onChange={() => setAddedToTrainingDataset({ ...addedToTrainingDataset, enabled: !addedToTrainingDataset.enabled })}
                    />
                    {addedToTrainingDataset.enabled && (
                        <div className="ml-6">
                            <CheckboxInput
                                label={`Set to ${addedToTrainingDataset.value ? "Yes" : "No"}`}
                                checked={addedToTrainingDataset.value}
                                onChange={() => setAddedToTrainingDataset({ ...addedToTrainingDataset, value: !addedToTrainingDataset.value })}
                            />
                        </div>
                    )}
                </div>

                <div className="space-y-1">
                    <CheckboxInput
                        label="Training Dataset Name"
                        checked={trainingDatasetName.enabled}
                        onChange={() => setTrainingDatasetName({ ...trainingDatasetName, enabled: !trainingDatasetName.enabled })}
                    />
                    {trainingDatasetName.enabled && (
                        <div className="ml-6">
                            <Input
                                value={trainingDatasetName.value}
                                onChange={(e) => setTrainingDatasetName({ ...trainingDatasetName, value: e.target.value })}
                                placeholder="e.g. light_rail"
                                maxLength={100}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
};
